import React from 'react';
import { clsx } from 'clsx';
import {
  MapIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CalendarDaysIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { Plan } from '../../services/planService';
import { Button, Card, EmptyState } from '../ui';

interface PlansListProps {
  plans: Plan[];
  selectedPlanId: number | null;
  onSelectPlan: (plan: Plan) => void;
  onCreatePlan: () => void;
  onEditPlan: (plan: Plan) => void;
  onDeletePlan: (plan: Plan) => void;
  loading?: boolean;
}

const PlansList: React.FC<PlansListProps> = ({
  plans,
  selectedPlanId,
  onSelectPlan,
  onCreatePlan,
  onEditPlan,
  onDeletePlan,
  loading,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-24" />
        ))}
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <EmptyState
        icon={<MapIcon className="h-12 w-12" />}
        title="No plans yet"
        description="Create your first plan to get started with visual planning"
        action={
          <Button onClick={onCreatePlan} leftIcon={<PlusIcon className="h-5 w-5" />}>
            Create Plan
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      {plans.map((plan) => (
        <div
          key={plan.id}
          onClick={() => onSelectPlan(plan)}
          className={clsx(
            'group cursor-pointer rounded-xl border-2 p-4 transition-all duration-200',
            selectedPlanId === plan.id
              ? 'border-primary-500 bg-primary-50/50 shadow-md'
              : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={clsx(
                'font-semibold truncate',
                selectedPlanId === plan.id ? 'text-primary-700' : 'text-gray-900'
              )}>
                {plan.name}
              </h3>
              {plan.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                  {plan.description}
                </p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <CalendarDaysIcon className="h-3.5 w-3.5" />
                  {formatDate(plan.createdAt)}
                </span>
                <span className="flex items-center gap-1">
                  <RectangleGroupIcon className="h-3.5 w-3.5" />
                  {plan._count?.rectangles || plan.rectangles?.length || 0} nodes
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditPlan(plan);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePlan(plan);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlansList;

